import React, { Fragment, useState, useEffect } from 'react';
import {
  Gauge,
  Plus,
  Search,
  Edit2,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  X,
  Calendar,
  FileText,
  Download,
  Paperclip,
  Eye,
} from 'lucide-react';
import { calibrationsApi, usersApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields, formatDateBR } from '../utils/dateValidation';

const STATUS_LABELS = {
  active: { label: 'Ativo', color: 'bg-green-500/20 text-green-400' },
  inactive: { label: 'Inativo', color: 'bg-gray-500/20 text-gray-400' },
  in_calibration: { label: 'Em Calibração', color: 'bg-blue-500/20 text-blue-400' },
  maintenance: { label: 'Manutenção', color: 'bg-yellow-500/20 text-yellow-400' },
  discarded: { label: 'Descartado', color: 'bg-red-500/20 text-red-400' },
};

const RESULT_LABELS = {
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  approved_restrictions: { label: 'Aprovado c/ Restrições', color: 'bg-yellow-500/20 text-yellow-400' },
  reproved: { label: 'Reprovado', color: 'bg-red-500/20 text-red-400' },
};

const Calibrations = () => {
  const [activeTab, setActiveTab] = useState('instruments');
  const [instruments, setInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [calibrationHistory, setCalibrationHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, overdue: 0, due_soon: 0, critical: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [filters, setFilters] = useState({ status: '', overdue_only: false });
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [uploadingCertId, setUploadingCertId] = useState(null);
  const [viewingCertId, setViewingCertId] = useState(null);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [instrumentsRes, statsRes] = await Promise.all([
        calibrationsApi.listInstruments(filters),
        calibrationsApi.instrumentStats(),
      ]);
      setInstruments(instrumentsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadCalibrationHistory = async (instrumentId) => {
    try {
      const res = await calibrationsApi.listCalibrations(instrumentId);
      setCalibrationHistory(res.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleSelectInstrument = async (instrument) => {
    setSelectedInstrument(instrument);
    await loadCalibrationHistory(instrument.id);
    setActiveTab('history');
  };

  const handleEditInstrument = async (inst) => {
    try {
      const res = await calibrationsApi.getInstrument(inst.id);
      setEditingInstrument(res.data);
      setShowModal(true);
    } catch (error) {
      toast.error('Erro ao carregar dados do instrumento');
    }
  };

  const handleDownloadCertificate = async (cal) => {
    try {
      const res = await calibrationsApi.downloadCalibrationCertificate(cal.id);
      const fileName = `Certificado_${cal.certificate_number || cal.id}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar certificado');
    }
  };

  const handleViewCertificate = async (cal) => {
    setViewingCertId(cal.id);
    try {
      const res = await calibrationsApi.downloadCalibrationCertificate(cal.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after giving the new tab time to load the blob
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error('Erro ao visualizar certificado');
    } finally {
      setViewingCertId(null);
    }
  };

  const handleUploadCertificate = async (cal, file) => {
    if (!file) return;
    setUploadingCertId(cal.id);
    try {
      await calibrationsApi.uploadCalibrationCertificate(cal.id, file);
      toast.success('Documento anexado com sucesso');
      if (selectedInstrument) await loadCalibrationHistory(selectedInstrument.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao anexar documento'));
    } finally {
      setUploadingCertId(null);
    }
  };

  const handleRemoveCertificate = async (cal) => {
    setUploadingCertId(cal.id);
    try {
      await calibrationsApi.deleteCalibrationCertificate(cal.id);
      toast.success('Documento removido');
      if (selectedInstrument) await loadCalibrationHistory(selectedInstrument.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao remover documento'));
    } finally {
      setUploadingCertId(null);
    }
  };

  const handleDownloadAllCertificates = async () => {
    if (!selectedInstrument) return;
    if (calibrationHistory.length === 0) {
      toast.error('Nenhum certificado registrado para este instrumento');
      return;
    }
    setDownloadingAll(true);
    try {
      const res = await calibrationsApi.downloadAllCertificates(selectedInstrument.id);
      const fileName = `Certificados_${selectedInstrument.code || selectedInstrument.id}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar certificados');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleCreateInstrument = async (data) => {
    try {
      await calibrationsApi.createInstrument(data);
      toast.success('Instrumento criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar instrumento'));
    }
  };

  const handleUpdateInstrument = async (data) => {
    try {
      await calibrationsApi.updateInstrument(editingInstrument.id, data);
      toast.success('Instrumento atualizado com sucesso');
      setShowModal(false);
      setEditingInstrument(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteInstrument = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este instrumento?')) return;
    try {
      await calibrationsApi.deleteInstrument(id);
      toast.success('Instrumento excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir instrumento');
    }
  };

  const handleRegisterCalibration = async (data) => {
    try {
      await calibrationsApi.createCalibration(selectedInstrument.id, data);
      toast.success('Calibração registrada com sucesso');
      setShowCalibrationModal(false);
      loadData();
      await loadCalibrationHistory(selectedInstrument.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao registrar calibração'));
    }
  };

  const tabs = [
    { id: 'instruments', label: 'Instrumentos', icon: Gauge },
    { id: 'history', label: 'Histórico de Calibrações', icon: Calendar },
    { id: 'certificates', label: 'Certificados', icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="calibrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <Gauge className="text-green-500" />
            Gestão de Calibrações
          </h1>
          <p className="text-gray-400 mt-1">Controle de instrumentos e calibrações</p>
        </div>
        <button
          onClick={() => { setEditingInstrument(null); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-instrument-btn"
        >
          <Plus size={20} />
          Novo Instrumento
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total de Instrumentos</p>
          <p className="text-2xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-white">{stats.overdue}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencem em 30 dias</p>
          <p className="text-2xl font-bold text-white">{stats.due_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Críticos</p>
          <p className="text-2xl font-bold text-white">{stats.critical}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-green-400 border-green-500'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            <tab.icon size={20} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : activeTab === 'instruments' ? (
          <InstrumentsTable
            instruments={instruments}
            onSelect={handleSelectInstrument}
            onEdit={handleEditInstrument}
            onDelete={handleDeleteInstrument}
            filters={filters}
            setFilters={setFilters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        ) : activeTab === 'history' ? (
          !selectedInstrument ? (
            <div className="py-12 text-center text-gray-500">
              Selecione um instrumento na aba Instrumentos para ver o histórico de calibrações.
            </div>
          ) : (
            <CalibrationHistory
              instrument={selectedInstrument}
              history={calibrationHistory}
              onRegister={() => setShowCalibrationModal(true)}
            />
          )
        ) : !selectedInstrument ? (
          <div className="py-12 text-center text-gray-500">
            Selecione um instrumento na aba Instrumentos para ver os certificados.
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedInstrument?.name}</h3>
                <p className="text-sm text-gray-400">Tag: {selectedInstrument?.tag || '-'} · {selectedInstrument?.serial_number ? `S/N: ${selectedInstrument.serial_number}` : ''}</p>
              </div>
              <button
                onClick={handleDownloadAllCertificates}
                className="btn-primary flex items-center gap-2"
                disabled={downloadingAll || calibrationHistory.length === 0}
              >
                {downloadingAll ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                Baixar Todos os Certificados
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Data Calibração</th>
                    <th>Próxima</th>
                    <th>Resultado</th>
                    <th>Laboratório / Prestador</th>
                    <th>N° Certificado</th>
                    <th>Observações</th>
                    <th>Certificado</th>
                  </tr>
                </thead>
                <tbody>
                  {calibrationHistory.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-8 text-gray-500">Nenhum certificado de calibração registrado</td></tr>
                  ) : (
                    calibrationHistory.map(cal => (
                      <tr key={cal.id}>
                        <td className="text-white">{formatDateBR(cal.calibration_date)}</td>
                        <td className="text-gray-400">{formatDateBR(cal.next_calibration_date)}</td>
                        <td>
                          {cal.result && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${cal.result === 'approved' ? 'bg-green-500/20 text-green-400' :
                              cal.result === 'approved_restrictions' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-red-500/20 text-red-400'
                              }`}>
                              {cal.result === 'approved' ? 'Aprovado' : cal.result === 'approved_restrictions' ? 'Aprovado c/ Restrições' : 'Reprovado'}
                            </span>
                          )}
                        </td>
                        <td className="text-gray-400">{cal.provider || selectedInstrument?.calibration_provider || '-'}</td>
                        <td className="text-gray-400">{cal.certificate_number || '-'}</td>
                        <td className="text-gray-400">{cal.notes || '-'}</td>
                        <td>
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleViewCertificate(cal)}
                                disabled={viewingCertId === cal.id}
                                className="flex items-center gap-1 text-green-400 hover:text-green-300"
                                title="Visualizar certificado"
                              >
                                {viewingCertId === cal.id ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />}
                                Visualizar
                              </button>
                              <button
                                onClick={() => handleDownloadCertificate(cal)}
                                className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                                title="Baixar certificado em PDF"
                              >
                                <Download size={16} />
                                Baixar
                              </button>
                            </div>
                            {cal.has_certificate_file ? (
                              <div className="flex items-center gap-2 text-xs">
                                <span className="flex items-center gap-1 text-green-400 truncate max-w-[140px]" title={cal.certificate_file_name || 'Documento anexado'}>
                                  <Paperclip size={12} className="flex-shrink-0" />
                                  <span className="truncate">{cal.certificate_file_name || 'Anexado'}</span>
                                </span>
                                <button
                                  onClick={() => handleRemoveCertificate(cal)}
                                  disabled={uploadingCertId === cal.id}
                                  className="text-red-400 hover:text-red-300 flex-shrink-0"
                                  title="Remover documento anexado"
                                >
                                  {uploadingCertId === cal.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                </button>
                              </div>
                            ) : (
                              <label
                                className="flex items-center gap-1 text-xs text-gray-400 hover:text-white cursor-pointer w-fit"
                                title="Anexar documento do certificado (PDF ou imagem) — apenas um por calibração"
                              >
                                {uploadingCertId === cal.id ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />}
                                Anexar documento
                                <input
                                  type="file"
                                  accept=".pdf,.png,.jpg,.jpeg"
                                  style={{ display: 'none' }}
                                  disabled={uploadingCertId === cal.id}
                                  onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    e.target.value = '';
                                    if (f) handleUploadCertificate(cal, f);
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Instrument Modal */}
      {showModal && (
        <InstrumentModal
          instrument={editingInstrument}
          users={users}
          onClose={() => { setShowModal(false); setEditingInstrument(null); }}
          onSave={editingInstrument ? handleUpdateInstrument : handleCreateInstrument}
        />
      )}

      {/* Calibration Modal */}
      {showCalibrationModal && (
        <CalibrationModal
          instrument={selectedInstrument}
          onClose={() => setShowCalibrationModal(false)}
          onSave={handleRegisterCalibration}
        />
      )}
    </div>
  );
};

const InstrumentsTable = ({ instruments, onSelect, onEdit, onDelete, filters, setFilters, searchTerm, setSearchTerm }) => {
  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (inst) => <td className="font-mono text-white">{inst.code}</td> },
    { key: 'name', label: 'Nome', render: (inst) => (
      <td className="text-white font-medium">
        {inst.name}
        {inst.is_critical && <span className="ml-2 text-xs text-red-400">CRÍTICO</span>}
      </td>
    ) },
    { key: 'area', label: 'Área', render: (inst) => <td className="text-gray-400">{inst.area || '-'}</td> },
    { key: 'type', label: 'Tipo Medição', render: (inst) => <td className="text-gray-400">{inst.measurement_type || '-'}</td> },
    { key: 'status', label: 'Status', render: (inst) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[inst.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {STATUS_LABELS[inst.status]?.label || inst.status}
        </span>
      </td>
    ) },
    { key: 'next', label: 'Próx. Calibração', render: (inst) => (
      <td>
        <span className={inst.is_overdue ? 'text-red-400 font-semibold' : 'text-gray-400'}>
          {formatDateBR(inst.next_calibration_date)}
          {inst.is_overdue && <AlertCircle size={14} className="inline ml-1" />}
        </span>
      </td>
    ) },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (inst) => (
      <td>
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onEdit(inst)} className="p-1 text-gray-400 hover:text-blue-400">
            <Edit2 size={18} />
          </button>
          <button onClick={() => onDelete(inst.id)} className="p-1 text-gray-400 hover:text-red-400">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'calibrations', columns: COLUMNS });

  return (
  <div className="space-y-4">
    {/* Filters */}
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input pl-10 w-full"
        />
      </div>
      <select
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="input px-3 w-full sm:w-60"
      >
        <option value="">Todos os Status</option>
        <option value="active">Ativo</option>
        <option value="inactive">Inativo</option>
        <option value="in_calibration">Em Calibração</option>
        <option value="maintenance">Manutenção</option>
      </select>
      <label className="flex items-center gap-2 text-gray-400">
        <input
          type="checkbox"
          checked={filters.overdue_only}
          onChange={(e) => setFilters({ ...filters, overdue_only: e.target.checked })}
          className="form-checkbox text-green-500"
        />
        Apenas Vencidos
      </label>
      <div className="flex items-center">
        <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
      </div>
    </div>

    {/* Table */}
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            {visibleColumns.map(col => (
              <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {instruments.length === 0 ? (
            <tr>
              <td colSpan={visibleColumns.length} className="py-8 text-center text-gray-500">Nenhum instrumento encontrado</td>
            </tr>
          ) : (
            instruments.map((inst) => (
              <tr key={inst.id} onClick={() => onSelect(inst)}>
                {visibleColumns.map(col => (
                  <Fragment key={col.key}>{col.render(inst)}</Fragment>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
  );
};

const CalibrationHistory = ({ instrument, history, onRegister }) => (
  <div className="space-y-4">
    <div className="flex justify-between items-center">
      <div>
        <h3 className="text-lg font-semibold text-white">{instrument.name}</h3>
        <p className="text-sm text-gray-400">{instrument.code}</p>
      </div>
      <button onClick={onRegister} className="btn-primary flex items-center gap-2" data-testid="register-calibration-btn">
        <Plus size={20} />
        Registrar Calibração
      </button>
    </div>

    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Certificado</th>
            <th>Tipo</th>
            <th>Prestador</th>
            <th>Resultado</th>
            <th>Próx. Calibração</th>
          </tr>
        </thead>
        <tbody>
          {history.length === 0 ? (
            <tr>
              <td colSpan="6" className="py-8 text-center text-gray-500">Nenhuma calibração registrada</td>
            </tr>
          ) : (
            history.map((cal) => (
              <tr key={cal.id}>
                <td className="text-white">{formatDateBR(cal.calibration_date)}</td>
                <td className="text-gray-400">{cal.certificate_number || '-'}</td>
                <td className="text-gray-400 capitalize">{cal.calibration_type || '-'}</td>
                <td className="text-gray-400">{cal.provider || '-'}</td>
                <td>
                  <span className={`px-2 py-1 rounded-full text-xs ${RESULT_LABELS[cal.result]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                    {RESULT_LABELS[cal.result]?.label || cal.result}
                  </span>
                </td>
                <td className="text-gray-400">{formatDateBR(cal.next_calibration_date)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const InstrumentModal = ({ instrument, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: instrument?.name || '',
    description: instrument?.description || '',
    tag: instrument?.tag || '',
    serial_number: instrument?.serial_number || '',
    manufacturer: instrument?.manufacturer || '',
    model: instrument?.model || '',
    area: instrument?.area || '',
    location: instrument?.location || '',
    measurement_type: instrument?.measurement_type || '',
    range_min: instrument?.range_min || '',
    range_max: instrument?.range_max || '',
    unit: instrument?.unit || '',
    resolution: instrument?.resolution || '',
    resolution_unit: instrument?.resolution_unit || '',
    calibration_frequency: instrument?.calibration_frequency || 365,
    calibration_type: instrument?.calibration_type || 'internal',
    calibration_provider: instrument?.calibration_provider || '',
    acquisition_date: instrument?.acquisition_date || '',
    acceptance_criteria: instrument?.acceptance_criteria || '',
    critical_tolerance: instrument?.critical_tolerance || '',
    tolerance_range: instrument?.tolerance_range || '',
    lep: instrument?.lep || '',
    greatest_error: instrument?.greatest_error || '',
    greatest_uncertainty: instrument?.greatest_uncertainty || '',
    total_error: instrument?.total_error || '',
    certificate_validator_id: instrument?.certificate_validator_id || '',
    is_critical: instrument?.is_critical || false,
    responsible_id: instrument?.responsible_id || '',
    status: instrument?.status || 'active',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.range_min === '' || formData.range_max === '') {
      toast.error('Preencha a Faixa Mínima e a Faixa Máxima para continuar');
      return;
    }
    const dateError = validateDateFields([
      { value: formData.acquisition_date, label: 'Data de Aquisição' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      data.range_min = data.range_min !== '' ? parseFloat(data.range_min) : null;
      data.range_max = data.range_max !== '' ? parseFloat(data.range_max) : null;
      if (!data.calibration_frequency) data.calibration_frequency = null;
      if (!data.responsible_id) delete data.responsible_id;
      if (!data.certificate_validator_id) delete data.certificate_validator_id;
      if (!data.acquisition_date) delete data.acquisition_date;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {instrument ? 'Editar Instrumento' : 'Novo Instrumento'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tag</label>
              <input
                type="text"
                value={formData.tag}
                onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Número de Série</label>
              <input
                type="text"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fabricante</label>
              <input
                type="text"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Modelo</label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Área</label>
              <input
                type="text"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Localização</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Medição</label>
              <input
                type="text"
                value={formData.measurement_type}
                onChange={(e) => setFormData({ ...formData, measurement_type: e.target.value })}
                className="input w-full"
                placeholder="Ex: Temperatura, Pressão, Peso"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Unidade</label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="input w-full"
                placeholder="Ex: °C, bar, kg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Resolução - Valor</label>
              <input
                type="text"
                value={formData.resolution}
                onChange={(e) => setFormData({ ...formData, resolution: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Resolução - Unidade</label>
              <input
                type="text"
                value={formData.resolution_unit}
                onChange={(e) => setFormData({ ...formData, resolution_unit: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tolerância Crítica do Processo</label>
              <input
                type="text"
                value={formData.critical_tolerance}
                onChange={(e) => setFormData({ ...formData, critical_tolerance: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Campo de Tolerância</label>
              <input
                type="text"
                value={formData.tolerance_range}
                onChange={(e) => setFormData({ ...formData, tolerance_range: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">L.E.P</label>
              <input
                type="text"
                value={formData.lep}
                onChange={(e) => setFormData({ ...formData, lep: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Maior Erro</label>
              <input
                type="text"
                value={formData.greatest_error}
                onChange={(e) => setFormData({ ...formData, greatest_error: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Maior Incerteza</label>
              <input
                type="text"
                value={formData.greatest_uncertainty}
                onChange={(e) => setFormData({ ...formData, greatest_uncertainty: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Erro Total</label>
              <input
                type="text"
                value={formData.total_error}
                onChange={(e) => setFormData({ ...formData, total_error: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Faixa Mínima *</label>
              <input
                type="number"
                step="any"
                value={formData.range_min}
                onChange={(e) => setFormData({ ...formData, range_min: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Faixa Máxima *</label>
              <input
                type="number"
                step="any"
                value={formData.range_max}
                onChange={(e) => setFormData({ ...formData, range_max: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Frequência (dias)</label>
              <input
                type="number"
                value={formData.calibration_frequency}
                onChange={(e) => setFormData({ ...formData, calibration_frequency: parseInt(e.target.value) })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo de Calibração</label>
              <select
                value={formData.calibration_type}
                onChange={(e) => setFormData({ ...formData, calibration_type: e.target.value })}
                className="input w-full"
              >
                <option value="internal">Interna</option>
                <option value="external">Externa</option>
              </select>
            </div>
            {formData.calibration_type === 'external' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Laboratório/Prestador</label>
                <input
                  type="text"
                  value={formData.calibration_provider}
                  onChange={(e) => setFormData({ ...formData, calibration_provider: e.target.value })}
                  className="input w-full"
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data de Aquisição</label>
              <input
                type="date"
                value={formData.acquisition_date}
                onChange={(e) => setFormData({ ...formData, acquisition_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Critério de Aceitação</label>
              <input
                type="text"
                value={formData.acceptance_criteria}
                onChange={(e) => setFormData({ ...formData, acceptance_criteria: e.target.value })}
                className="input w-full"
                placeholder="Ex: ±0,5°C em relação ao padrão"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
              <select
                value={formData.responsible_id}
                onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Res. Por validar o Certificado</label>
              <select
                value={formData.certificate_validator_id}
                onChange={(e) => setFormData({ ...formData, certificate_validator_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            {instrument && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input w-full"
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                  <option value="in_calibration">Em Calibração</option>
                  <option value="maintenance">Manutenção</option>
                  <option value="discarded">Descartado</option>
                </select>
              </div>
            )}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-gray-400">
                <input
                  type="checkbox"
                  checked={formData.is_critical}
                  onChange={(e) => setFormData({ ...formData, is_critical: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Instrumento Crítico
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const CalibrationModal = ({ instrument, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    calibration_date: new Date().toISOString().split('T')[0],
    certificate_number: '',
    calibration_type: instrument?.calibration_type || 'internal',
    provider: instrument?.calibration_provider || '',
    technician: '',
    result: 'approved',
    uncertainty: '',
    standard_used: '',
    environmental_conditions: '',
    observations: '',
    next_calibration_date: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.next_calibration_date, label: 'Próx. Calibração' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    setSaving(true);
    try {
      const data = { ...formData };
      if (!data.next_calibration_date) delete data.next_calibration_date;
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Registrar Calibração</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data da Calibração *</label>
              <input
                type="date"
                value={formData.calibration_date}
                onChange={(e) => setFormData({ ...formData, calibration_date: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Nº Certificado</label>
              <input
                type="text"
                value={formData.certificate_number}
                onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <select
                value={formData.calibration_type}
                onChange={(e) => setFormData({ ...formData, calibration_type: e.target.value })}
                className="input w-full"
              >
                <option value="internal">Interna</option>
                <option value="external">Externa</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Prestador/Laboratório</label>
              <input
                type="text"
                value={formData.provider}
                onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Técnico</label>
              <input
                type="text"
                value={formData.technician}
                onChange={(e) => setFormData({ ...formData, technician: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Resultado *</label>
              <select
                value={formData.result}
                onChange={(e) => setFormData({ ...formData, result: e.target.value })}
                className="input w-full"
                required
              >
                <option value="approved">Aprovado</option>
                <option value="approved_restrictions">Aprovado com Restrições</option>
                <option value="reproved">Reprovado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Incerteza</label>
              <input
                type="text"
                value={formData.uncertainty}
                onChange={(e) => setFormData({ ...formData, uncertainty: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Próx. Calibração</label>
              <input
                type="date"
                value={formData.next_calibration_date}
                onChange={(e) => setFormData({ ...formData, next_calibration_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Padrão Utilizado</label>
              <input
                type="text"
                value={formData.standard_used}
                onChange={(e) => setFormData({ ...formData, standard_used: e.target.value })}
                className="input w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Observações</label>
              <textarea
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                className="input w-full"
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" size={20} /> : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Calibrations;
